import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ShoppingCart, Plus } from 'lucide-react'
import type { Product } from '../types'
import FavoriteProductItem from '../features/catalog/FavoriteProductItem'
import FavoritesDrawerFooter from '../features/catalog/FavoritesDrawerFooter'

interface Props {
  open: boolean
  onClose: () => void
  products: Product[]
  onRemove: (id: string) => void
  onClearAll: () => void
  onNavigate: (id: string) => void
}

export default function FavoritesDrawer({ open, onClose, products, onRemove, onClearAll, onNavigate }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null)
  const [quantities, setQuantities] = useState<Record<string, number>>({})

  const setQuantity = (id: string, qty: number) => {
    setQuantities(prev => ({ ...prev, [id]: qty }))
  }

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      drawerRef.current?.focus()
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[60]"
            onClick={onClose}
            aria-hidden="true"
          />

            <motion.div
              key="drawer"
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="רשימת מוצרים"
              tabIndex={-1}
              dir="rtl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32, mass: 0.9 }}
              drag="x"
              dragConstraints={{ left: 0, right: 400 }}
              dragElastic={{ left: 0, right: 0.3 }}
              onDragEnd={(_, info) => {
                if (info.offset.x > 100 || info.velocity.x > 500) onClose()
              }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-full lg:max-w-[400px] z-[70]
                        bg-[#FAF7F2] shadow-[-8px_0_40px_rgba(44,24,16,0.12)]
                        flex flex-col cursor-grab active:cursor-grabbing"
            >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#F2EDE4]">
              <div className="flex items-center gap-4 lg:gap-3">
                <div className="w-10 h-10 lg:w-9 lg:h-9 rounded-xl bg-[#D8F3DC] flex items-center justify-center">
                  <ShoppingCart className="w-6.5 h-6.5 lg:w-5 lg:h-5 text-green-primary fill-[#D8F3DC]" />
                </div>
                <div>
                  <h2 className="text-[17px] lg:text-[16px] font-bold text-[#1A1A1A] leading-none">רשימת מוצרים</h2>
                  <p className="text-[13px] lg:text-[12px] text-[#6E6660] mt-0.5">
                    {products.length === 0
                      ? 'אין מוצרים עדיין'
                      : `${products.length} מוצר${products.length !== 1 ? 'ים' : ''}`}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-[#F2EDE4] flex items-center justify-center
                           transition-colors border-none bg-transparent cursor-pointer"
                aria-label="סגור"
              >
                <X className="w-6 h-6 lg:w-4 lg:h-4 text-[#6B6560]" />
              </button>
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
              {products.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center -mt-16 px-4 pb-16 overflow-y-auto">
                  <div className="w-16 h-16 rounded-2xl bg-[#F2EDE4] flex items-center justify-center mb-4">
                    <ShoppingCart className="w-8 h-8 text-[#D4C5B5]" />
                  </div>
                  <p className="text-[17px] font-semibold text-[#1A1A1A] mb-3.5">אין מוצרים עדיין</p>
                  <div className="flex flex-col gap-4 items-center justify-center text-[18px] text-[#6E6660] leading-relaxed max-w-[220px]">
                    <div className="flex gap-3 items-center">
                      <span>לחץ על</span>
                      <div className="w-10 h-10 lg:w-9 lg:h-9 rounded-xl bg-[#D8F3DC] flex items-center justify-center">
                        <Plus className="w-5 h-5 text-green-primary" />
                      </div>
                    </div>
                    <span>על כל מוצר בקטלוג כדי להוסיף לרשימת המועדפים</span>
                  </div>
                </div>
              ) : (
                <AnimatePresence>
                  {products.map(product => (
                    <FavoriteProductItem
                    key={product.id}
                    product={product}
                    quantity={quantities[product.id]}
                    onSetQuantity={(qty) => setQuantity(product.id, qty)}
                    onNavigate={onNavigate}
                    onRemove={onRemove}
                    onClose={onClose}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>

            {products.length > 0 && (
              <FavoritesDrawerFooter
                products={products}
                quantities={quantities}
                onClearAll={onClearAll}
              />
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}